import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import React from 'react';
import { FlatList, View } from 'react-native';
import { StepCard } from '../../step-card/StepCard';
import { BoxStyleInfo, FormatInfo, LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import i18n from 'src/app/i18n';
import { observer } from 'mobx-react-lite';
import { stylesDesktop, stylesMobile } from './OptionStep.style';
import { useOptionStep } from './OptionStep.logic';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { LinearGradient } from 'expo-linear-gradient';
import { CircularButton } from '@photosi/rn-components/buttons/circular-button/CircularButton';
import {
  CIRCULAR_BUTTON_SIZES,
  CIRCULAR_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/circular-button/CircularButton.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { brand } from 'src/theme/colors';
import { BUTTON_SIZES } from '../../buttons/button/Button.constants';

export interface OptionStepProps {
  cards: FormatInfo[] | BoxStyleInfo[] | LookupChoiceForStep[];
  onConfirmStep: () => void;
  selectedCard?: string;
  setSelectedCard: (code: string) => void;
  title: string;
  type: 'FormatInfo' | 'BoxStyleInfo' | 'LookupChoiceForStep' | 'PaperType' | 'PaperFinish';
  groupCode?: string;
}

export const OptionStep = observer(
  ({ cards, onConfirmStep, selectedCard, setSelectedCard, title, type, groupCode }: OptionStepProps) => {
    const {
      isDesktop,
      isFewItems,
      numColumns,
      flatListRef,
      ITEM_SPACING,
      sidePadding,
      renderItem,
      getItemLayout,
      scrollToIndex,
      activeIndex,
      onScrollHandler,
      ITEM_FULL_HEIGHT,
      imageHeight,
    } = useOptionStep({ cards, selectedCard, setSelectedCard, type, onConfirmStep, groupCode });

    const styles = isDesktop ? stylesDesktop : stylesMobile;

    return (
      <>
        <View style={{ flex: 1 }}>
          <View
            style={[
              {
                justifyContent: 'center',
                alignItems: 'center',
                paddingBottom: 16,
              },
              isDesktop && { flex: 0.5 },
            ]}
          >
            <TextLato style={styles.title} accessibilityRole="header">
              {title}
            </TextLato>
          </View>

          {!isDesktop && (
            <LinearGradient
              pointerEvents="none"
              colors={['rgba(255,255,255,1)', 'rgba(255,255,255,0)']}
              style={{
                position: 'absolute',
                top: 35,
                left: 0,
                right: 0,
                height: 30,
                zIndex: 1,
              }}
            />
          )}

          <FlatList
            key={numColumns}
            ref={flatListRef}
            data={cards}
            accessibilityLiveRegion="polite"
            horizontal={isDesktop}
            numColumns={isDesktop ? undefined : numColumns}
            keyExtractor={item => item.productConfigurationId}
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            renderItem={({ item, index }) => {
              const props = renderItem({ item, index });
              return (
                <StepCard
                  {...props}
                  imageHeight={imageHeight}
                  hideIcon={['PaperType', 'PaperFinish'].includes(type)}
                  boldOnSelect={!['PaperType', 'PaperFinish'].includes(type)}
                />
              );
            }}
            extraData={selectedCard}
            getItemLayout={getItemLayout}
            ItemSeparatorComponent={() => <View style={{ width: ITEM_SPACING }} />}
            columnWrapperStyle={
              isDesktop || isFewItems
                ? undefined
                : {
                    gap: 24,
                  }
            }
            contentContainerStyle={
              isDesktop
                ? {
                    paddingHorizontal: sidePadding,
                    paddingVertical: 4,
                    ...(isFewItems && { flexGrow: 1 }),
                  }
                : {
                    marginTop: 10,
                    gap: 24,
                    paddingBottom: ITEM_FULL_HEIGHT * 0.4,
                    paddingHorizontal: isFewItems ? 16 : 4,
                    paddingVertical: 4,
                  }
            }
            onScroll={onScrollHandler}
          />
        </View>
        {isDesktop ? (
          !isFewItems &&
          cards.length !== 0 && (
            <>
              <LinearGradient
                pointerEvents="none"
                colors={['rgba(240,245,247,1)', 'rgba(240,245,247,0.6)', 'rgba(240,245,247,0)']}
                locations={[0, 0.6, 1]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{
                  position: 'absolute',
                  width: 90,
                  height: ITEM_FULL_HEIGHT,
                  zIndex: 1,
                }}
              />
              {activeIndex > 1 && (
                <CircularButton
                  onPress={async () => scrollToIndex(activeIndex - 1)}
                  size={CIRCULAR_BUTTON_SIZES.EXTRALARGE}
                  type={CIRCULAR_BUTTON_TYPES.FILL}
                  iconName={ICON_NAMES.ArrowLeft2}
                  focusGroup={FocusGroups.AFTER_PRODUCT}
                  testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.LEFT_ARROW)}
                  focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.LEFT_ARROW)}
                  focusOrder={0}
                  iconStyle={{ color: brand.main.water }}
                  style={[{ position: 'absolute', left: 68 }]}
                  accessibilityLabel={i18n.t('modal-configuration-steps:option-prev')}
                />
              )}
              {activeIndex <= cards.length - 3 && (
                <CircularButton
                  onPress={async () => scrollToIndex(activeIndex + 1)}
                  size={CIRCULAR_BUTTON_SIZES.EXTRALARGE}
                  type={CIRCULAR_BUTTON_TYPES.FILL}
                  iconName={ICON_NAMES.ArrowRight2}
                  focusGroup={FocusGroups.AFTER_PRODUCT}
                  testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.RIGHT_ARROW)}
                  focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.RIGHT_ARROW)}
                  focusOrder={1}
                  iconStyle={{ color: brand.main.water }}
                  style={{ position: 'absolute', right: 68 }}
                  accessibilityLabel={i18n.t('modal-configuration-steps:option-next')}
                />
              )}
              <LinearGradient
                pointerEvents="none"
                colors={['rgba(240,245,247,0)', 'rgba(240,245,247,0.6)', 'rgba(240,245,247,1)']}
                locations={[0, 0.4, 1]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{
                  position: 'absolute',
                  right: 0,
                  width: 90,
                  height: ITEM_FULL_HEIGHT,
                  zIndex: 1,
                }}
              />
            </>
          )
        ) : (
          <LinearGradient
            pointerEvents="none"
            colors={['rgba(255,255,255,0)', 'rgba(255,255,255,1)']}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 50,
              zIndex: 1,
            }}
          />
        )}

        <View style={styles.buttonContainer}>
          <PrimaryButton
            onPress={async () => onConfirmStep()}
            style={styles.button}
            testID={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.CONTINUE)}
            focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, TestIDs.CONTINUE)}
            focusGroup={FocusGroups.AFTER_PRODUCT}
            text={i18n.t('modal-configuration-steps:continue')}
            focusOrder={2}
            size={BUTTON_SIZES.REGULAR}
          />
        </View>
      </>
    );
  },
);
